import { useState } from 'react';
import { toast } from 'sonner';

import { useReactionMutation } from '@/hooks/use-content';
import { haptic } from '@/lib/telegram';
import { cls } from '@/lib/format';
import type { ContentReactionType } from '@/types/content';

// SPEC-021: Reaction bar — like + bookmark toggles with optimistic UI.
// The backend stores reactions per (user, content, type), so toggling
// hits /reactions (POST) or /reactions/{type} (DELETE).

interface ReactionBarProps {
  contentId: string;
  initialLiked?: boolean;
  initialBookmarked?: boolean;
}

export function ReactionBar({
  contentId,
  initialLiked = false,
  initialBookmarked = false,
}: ReactionBarProps) {
  const [liked, setLiked] = useState(initialLiked);
  const [bookmarked, setBookmarked] = useState(initialBookmarked);
  const mutation = useReactionMutation();

  const toggle = async (
    type: ContentReactionType,
    next: boolean,
    setter: (v: boolean) => void,
  ) => {
    haptic('selection');
    setter(next); // optimistic
    try {
      await mutation.mutateAsync({
        contentId,
        type,
        action: next ? 'add' : 'remove',
      });
    } catch {
      setter(!next); // rollback
      toast.error(`Couldn't update ${type}`);
    }
  };

  return (
    <div className="flex items-center gap-2 px-4">
      <button
        type="button"
        onClick={() => toggle('like', !liked, setLiked)}
        className={cls(
          'flex items-center gap-1 rounded-full border border-[var(--color-separator)] px-3 py-1.5 text-sm transition-colors',
          liked
            ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-white'
            : 'text-[var(--color-fg)]',
        )}
      >
        <span>{liked ? '❤️' : '🤍'}</span>
        <span>Like</span>
      </button>
      <button
        type="button"
        onClick={() => toggle('bookmark', !bookmarked, setBookmarked)}
        className={cls(
          'flex items-center gap-1 rounded-full border border-[var(--color-separator)] px-3 py-1.5 text-sm transition-colors',
          bookmarked
            ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-white'
            : 'text-[var(--color-fg)]',
        )}
      >
        <span>{bookmarked ? '🔖' : '📑'}</span>
        <span>Save</span>
      </button>
    </div>
  );
}
